import {config} from '@/config';
import {objGet, objHas} from '@/lib/utils';
import {useAuthStore} from '@/stores/auth-store';
import {setLogoutRedirect} from '@/stores/ui-store';
import {ItemUpdate, LightstreamerClient, Subscription, SubscriptionListener} from 'lightstreamer-client-web';
import {toast} from 'sonner';

export type LsMode = 'COMMAND' | 'MERGE' | 'DISTINCT' | 'RAW';

// noinspection SpellCheckingInspection
export const LS_SUB_ITEMS = {
  INDIVIDUAL_OFFERS: 'INDIVIDUAL_OFFERS',
  INDIVIDUAL_OFFERS_DEMO: 'INDIVIDUAL_OFFERS_DEMO',
  BUNDLED_OFFERS: 'BUNDLED_OFFERS',
  BUNDLED_OFFERS_DEMO: 'BUNDLED_OFFERS_DEMO',
  OFFER_BID: 'OFFER_BID_',
  OFFER_BID_DEMO: 'DOFFER_BID_',
  OFFER_ASK: 'OFFER_ASK_',
  OFFER_ASK_DEMO: 'DOFFER_ASK_',
};

type SubscriptionItem = {
  id: string;
  subscription: Subscription;
};

class LsClient {
  private readonly client: LightstreamerClient;
  private readonly dataAdapter: string;
  private connected: boolean = false;
  private token: string | null = null;
  private userId: string | null = null;
  private disconnectedAt: Date | null = null;
  private subscriptions: SubscriptionItem[] = [];

  constructor() {
    if (config.env === 'development' && !config.ls.url) {
      return;
    }
    this.client = new LightstreamerClient(config.ls.url, config.ls.adapterSet);
    this.client.addListener({
      onServerError: async (errorCode: number, errorMessage: string) => {
        console.error(errorCode, errorMessage);
        if (!useAuthStore.getState().doingLogout) {
          if (errorCode === 1) { // user token expired
            setLogoutRedirect(true, 'LS Server Error: ' + errorMessage);
            toast.error('LS Server Error: ' + errorMessage);
          }
        }
      },
      onStatusChange: async (status: string) => {
        // console.log("LS Status: ", status);
        if (status.includes('DISCONNECTED')) {
          this.connected = false;
          if (config.env === 'development') {
            this.client.disconnect();
            return;
          }
          const anHour = 60 * 60 * 1000; // an hour: 60 minutes * 60 seconds * 1000 milliseconds
          if (this.disconnectedAt == null || (new Date().getTime() - this.disconnectedAt.getTime()) > anHour) {
            this.disconnectedAt = new Date();
            // addAppToast({variant: "error", title: "LS Disconnected", description: "LS connection lost!"});
          }
        }
      },
    });
    this.dataAdapter = config.ls.dataAdapter;
  }

  connect(userId: string, token: string) {
    if (!this.client || !userId || !token) {
      return;
    }
    if (this.connected && this.token == token && this.userId == userId) {
      return;
    }
    this.userId = userId;
    this.token = token;
    this.client.connectionDetails.setUser(this.userId);
    this.client.connectionDetails.setPassword(this.token);
    this.client.connect();
    this.connected = true;
  }

  disconnect() {
    if (this.connected) {
      if (this.subscriptions.length > 0) {
        this.subscriptions.forEach(s => {
          this.unsubscribe(s.id);
        });
      }
      this.client.disconnect();
      this.connected = false;
      this.userId = null;
      this.token = null;
    }
  }

  subscribe(mode: LsMode, subId: string, fields: string[] = [], secondLevelFields?: string[]) {
    if (!this.connected) {
      return null;
    }
    if (fields.indexOf('key') === -1) {
      fields.push('key');
    }
    if (fields.indexOf('command') === -1) {
      fields.push('command');
    }
    if (fields.indexOf('count') === -1) {
      fields.push('count');
    }
    this.unsubscribe(subId);
    const subscription = new Subscription(mode, subId, fields);
    subscription.setDataAdapter(this.dataAdapter);
    if (secondLevelFields) {
      subscription.setCommandSecondLevelDataAdapter(this.dataAdapter);
      subscription.setCommandSecondLevelFields(secondLevelFields);
    }

    this.client.subscribe(subscription);
    this.subscriptions.push({id: subId, subscription});

    return subscription;
  }

  unsubscribe(subId: string) {
    if (this.connected) {
      const subIndex = this.subscriptions.findIndex(s => s.id === subId);
      if (subIndex !== -1) {
        this.client.unsubscribe(this.subscriptions[subIndex].subscription);
        this.subscriptions.splice(subIndex, 1);
      }
    }
  }

  hasSubscription(subId: string) {
    return this.subscriptions.findIndex(s => s.id === subId) !== -1;
  }

  subscribeItems(items: string[], fields: string[], listener: SubscriptionListener) {
    const subscription = new Subscription("MERGE", items, fields);
    subscription.setDataAdapter(this.dataAdapter);
    subscription.setRequestedSnapshot("yes");
    subscription.addListener(listener);
    this.client.subscribe(subscription);
    return subscription;
  }
}

export default new LsClient();

type LsCommand = 'ADD' | 'UPDATE' | 'DELETE';

// noinspection JSUnusedGlobalSymbols
export class LsUpdate<T = any> {
  private itemUpdate: ItemUpdate;
  public key: string;
  public command: LsCommand;
  public count: number;
  public changed: any = {};
  public data: any = {};

  constructor(itemUpdate: ItemUpdate) {
    try {
      this.itemUpdate = itemUpdate;
      this.itemUpdate.forEachChangedField((fieldName, _fieldPos, value) => {
        this.changed[fieldName] = value;
      });
      this.itemUpdate.forEachField((fieldName, _fieldPos, value) => {
        this.data[fieldName] = value;
      });
      if ("key" in this.data) {
        this.key = this.data["key"];
      } else if ("id" in this.data) {
        this.key = this.data["id"];
      }
      if ("command" in this.data) {
        this.command = this.data["command"];
      }
      if ("count" in this.data) {
        this.count = this.data["count"];
      }
    } catch (error) {
      console.error(error);
    }
  }

  hasData<K extends keyof T>(key: K) {
    return objHas(this.data, key);
  }

  get<K extends keyof T>(key: K) {
    return objGet(this.data, key);
  }

  hasChanged<K extends keyof T>(key: K): boolean {
    return objHas(this.changed, key);
  }

  isAdd() {
    return this.command === 'ADD';
  }

  isUpdate() {
    return this.command === 'UPDATE';
  }

  isDelete() {
    return this.command === 'DELETE';
  }

  isSnapshot() {
    return this.itemUpdate.isSnapshot();
  }

  isNew() {
    return this.isUpdate() && this.isSnapshot();
  }
}